import { GeneratedCollection } from "../generated.collection.abstraction";
import { inject, signal, Signal, WritableSignal } from "@angular/core";
import { Observable } from "rxjs";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { toObservable } from "@angular/core/rxjs-interop";
import { AccountView } from "@bitwarden/web-vault/app/models/view/account/account.view";
import { InstitutionInterestView } from "@bitwarden/web-vault/app/models/view/institution/institution-interest.view";
import { InstitutionView } from "@bitwarden/web-vault/app/models/view/institution/institution.view";
import { GLOSS_VAULT_ACCOUNT_ID_SUFFIX } from "@bitwarden/web-vault/app/shared/utils/helper.institution/gloss-vault-institution.helper";

export class EnrichAccountGeneratedCollection extends GeneratedCollection<AccountView> {
  protected log: LogService = inject(LogService);
  protected _collection: WritableSignal<AccountView[]> = signal<AccountView[]>(null);
  collection: Signal<AccountView[]> = this._collection.asReadonly();
  collection$: Observable<AccountView[]> = toObservable(this.collection);

  /**
    TODO: We need to remove c_institution and c_institutionAccountType from AccountView after migrating all usages
    and then delete this collection because we should NOT be creating a stale copy of the InstituitionView data.
    This will lead to data falling out of sync when the store is updated or will lead to unnecessary complexity when
    trying to keep both in sync.
    We should always be looking up the InstitutionView data from the InstitutionsStoreCollection when needed and records
    should only hold the ID as a link back to the other store collection.
   **/
  generate(institutions: InstitutionView[], accounts: AccountView[]): void {
    this.log.info("---- Regenerating Enrich Accounts Collection ---- ");

    const enrichAccounts: AccountView[] = [];

    for (const account of accounts) {
      const enrich = account.clone();
      enrich.c_institution = institutions.find(
        (i) => i.id === enrich.institutionLink.institutionId,
      );
      enrich.c_institutionAccountType = enrich.c_institution?.availableAccounts?.find(
        (instoAcc) => instoAcc.id === enrich.institutionLink.institutionAccountId,
      );
      enrich.c_maxInterestRate = this.getMaxInterestRate(enrich);

      // TODO: Remove this after the Gloss Credits account is enabled for update
      const instAccId = enrich.institutionLink?.institutionAccountId;
      if (instAccId && instAccId.endsWith(GLOSS_VAULT_ACCOUNT_ID_SUFFIX)) {
        enrich.name = "GLOSS Credits";
      }
      enrichAccounts.push(this.deepFreeze(enrich));
    }

    this.update(enrichAccounts);
    this.log.info("---- Regenerating Enrich Accounts Collection End---- ");
  }

  clear(): void {
    this._collection.set(null);
  }

  update(collection: AccountView[]): void {
    this._collection.set(collection);
  }

  private getMaxInterestRate(account: AccountView): number {
    const interestRates = account.c_institutionAccountType?.interestRates;

    if (!interestRates?.length) {
      return Number.NaN;
    }

    const matchingRates = interestRates.filter((rate) => rate.symbol === account.currency);
    const ratesToUse = matchingRates.length ? matchingRates : interestRates;

    return ratesToUse.reduce((maxRate: number, currentRate: InstitutionInterestView) => {
      return currentRate.rate > maxRate ? currentRate.rate : maxRate;
    }, ratesToUse[0].rate);
  }
}
